import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import {
  ArrowRight, Sparkles, Shield, Clock, CheckCircle2, Award, ImageOff,
} from "lucide-react";

const HERO =
  "https://images.pexels.com/photos/6873011/pexels-photo-6873011.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CTA_IMG =
  "https://images.pexels.com/photos/6873063/pexels-photo-6873063.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FORMULAS = [
  {
    id: "express",
    emoji: "🚗",
    name: "Formule Express",
    price: "25 €",
    subtitle: "L'essentiel, en un clin d'œil",
    features: [
      "Aspiration de l'habitacle",
      "Dépoussiérage des plastiques",
      "Nettoyage des vitres intérieures",
    ],
  },
  {
    id: "interieur",
    emoji: "✨",
    name: "Formule Intérieur",
    price: "45 €",
    subtitle: "Un intérieur comme neuf",
    features: [
      "Aspiration complète",
      "Nettoyage des plastiques",
      "Nettoyage des vitres",
      "Nettoyage du coffre",
      "Désodorisation",
    ],
  },
  {
    id: "exterieur",
    emoji: "🚿",
    name: "Formule Extérieur",
    price: "35 €",
    subtitle: "Carrosserie éclatante",
    features: [
      "Prélavage",
      "Lavage à la main",
      "Nettoyage des jantes",
      "Nettoyage des pneus",
      "Séchage microfibre",
    ],
  },
  {
    id: "complete",
    emoji: "⭐",
    name: "Formule Complète",
    price: "70 €",
    subtitle: "Intérieur + Extérieur",
    features: ["Tout le contenu de la formule Intérieur", "Tout le contenu de la formule Extérieur"],
    highlight: true,
  },
  {
    id: "premium",
    emoji: "👑",
    name: "Formule Premium",
    price: "100 €",
    subtitle: "Le grand soin",
    features: [
      "Nettoyage complet intérieur",
      "Nettoyage complet extérieur",
      "Shampouineuse sièges et moquettes",
      "Désodorisation",
      "Finitions soignées",
    ],
  },
];

const OPTIONS = [
  { label: "Poils d'animaux", price: "+ 15 à 30 €" },
  { label: "Shampoing d'un siège", price: "+ 15 €" },
  { label: "Nettoyage moquette / coffre", price: "+ 20 €" },
  { label: "Désodorisation", price: "+ 10 €" },
  { label: "Utilitaire / 7 places", price: "+ 15 à 25 €" },
];

const WHY = [
  { icon: Sparkles, title: "Réalisé avec soin", text: "Chaque détail compte — nous travaillons comme si c'était notre propre voiture." },
  { icon: Award, title: "Produits professionnels", text: "Cires, shampooings et microfibres haut de gamme — respectueux du véhicule." },
  { icon: Shield, title: "Travail minutieux", text: "Attention aux zones oubliées : plastiques, contours, jantes, joints." },
  { icon: CheckCircle2, title: "Tarifs transparents", text: "Pas de surprise à la fin — le prix affiché est celui payé." },
  { icon: Clock, title: "Sur rendez-vous", text: "À domicile ou sur votre lieu de travail, on s'adapte à votre planning." },
  { icon: Sparkles, title: "Service de qualité", text: "Une satisfaction totale ou nous revenons parfaire la prestation." },
];

export default function ServiceNettoyage() {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="service-nettoyage-page">
      <SEO
        title="Nettoyage & Préparation Automobile"
        description="Nettoyage intérieur et extérieur de votre véhicule à Orléans et dans toute la France. Formules à partir de 25 € — Express, Intérieur, Extérieur, Complète, Premium."
      />
      <Header variant="nettoyage" />

      {/* HERO */}
      <section
        className="relative pt-32 pb-32 overflow-hidden grain"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(5,5,5,.92) 0%, rgba(5,5,5,.65) 55%, rgba(5,5,5,.3) 100%), url(${HERO})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 reveal">
          <div className="text-xs tracking-[0.55em] gold uppercase mb-6">
            Service #04 — Nettoyage
          </div>
          <h1 className="font-serif text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-3xl">
            Nettoyage & <span className="gold">Préparation</span><br />
            esthétique automobile
          </h1>
          <p className="mt-8 text-lg text-gray-300 max-w-2xl leading-relaxed">
            Offrez une seconde jeunesse à votre véhicule. Entretien régulier,
            remise en état avant vente, ou simplement pour retrouver le
            plaisir d'un habitacle propre — nos formules s'adaptent à chaque
            besoin.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/nettoyage"
              data-testid="nettoyage-cta-primary"
              className="btn-gold inline-flex items-center gap-2"
            >
              Réserver un nettoyage <ArrowRight size={18} />
            </Link>
            <a href="#formules" className="btn-outline-gold inline-flex items-center gap-2">
              Voir les formules
            </a>
          </div>
        </div>
      </section>

      {/* Formules */}
      <section id="formules" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.5em] gold uppercase text-center">Nos formules</div>
          <h2 className="mt-3 font-serif text-white text-4xl sm:text-5xl text-center">
            Choisissez la vôtre.
          </h2>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FORMULAS.map((f) => (
              <FormulaCard key={f.id} {...f} />
            ))}
          </div>

          {/* Options */}
          <div className="mt-16 border border-white/10 bg-[#0a0a0a] p-8 md:p-10">
            <div className="text-xs tracking-widest gold uppercase mb-4">Options complémentaires</div>
            <h3 className="font-serif text-2xl text-white mb-6">
              Personnalisez votre prestation.
            </h3>
            <ul className="grid sm:grid-cols-2 gap-3">
              {OPTIONS.map((o) => (
                <li
                  key={o.label}
                  className="flex justify-between items-baseline py-3 border-b border-white/5"
                >
                  <span className="text-gray-300">{o.label}</span>
                  <span className="font-serif text-lg gold">{o.price}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Why */}
      <section className="py-24 border-t border-white/5 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.5em] gold uppercase text-center">Pourquoi nous choisir</div>
          <h2 className="mt-3 font-serif text-white text-4xl sm:text-5xl text-center mb-16">
            Le nettoyage haut de gamme, sans concession.
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {WHY.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="p-8 border border-white/10 bg-black hover:border-[color:var(--gold)]/40 transition-colors duration-500"
              >
                <Icon size={26} className="gold mb-4" />
                <div className="font-serif text-xl text-white mb-2">{title}</div>
                <p className="text-sm text-gray-400 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Galerie avant/après placeholder */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs tracking-[0.5em] gold uppercase text-center">Galerie</div>
          <h2 className="mt-3 font-serif text-white text-4xl sm:text-5xl text-center">
            Avant / Après
          </h2>
          <p className="mt-3 text-center text-gray-400">
            Nos réalisations arrivent bientôt.
          </p>

          <div className="mt-12 grid md:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                data-testid={`gallery-slot-${i}`}
                className="aspect-[4/3] border border-dashed border-white/10 bg-[#0a0a0a] flex flex-col items-center justify-center text-gray-600"
              >
                <ImageOff size={28} className="mb-3 opacity-50" />
                <div className="text-xs uppercase tracking-widest">Bientôt disponible</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="py-24 border-t border-white/5 bg-black relative overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(90deg, rgba(0,0,0,.9), rgba(0,0,0,.55)), url(${CTA_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center relative">
          <div className="text-xs tracking-[0.5em] gold uppercase">Prêt ?</div>
          <h2 className="mt-3 font-serif text-white text-4xl sm:text-5xl">
            Redonnez à votre véhicule<br />tout son éclat.
          </h2>
          <Link
            to="/nettoyage"
            data-testid="nettoyage-cta-final"
            className="btn-gold inline-flex items-center gap-2 mt-8"
          >
            Prendre rendez-vous <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function FormulaCard({ id, emoji, name, price, subtitle, features, highlight }) {
  return (
    <div
      data-testid={`formula-card-${id}`}
      className={`relative p-8 border transition-all duration-500 flex flex-col ${
        highlight
          ? "border-[color:var(--gold)]/60 bg-gradient-to-br from-[#131007] to-[#0a0a0a]"
          : "border-white/10 bg-[#0a0a0a] hover:border-[color:var(--gold)]/40"
      }`}
    >
      {highlight && (
        <div className="absolute -top-3 left-8 bg-[color:var(--gold)] text-black text-[10px] tracking-[0.35em] uppercase px-3 py-1">
          Recommandée
        </div>
      )}
      <div className="text-4xl mb-3">{emoji}</div>
      <h3 className="font-serif text-2xl text-white">{name}</h3>
      <div className="text-xs text-gray-500 mt-1">{subtitle}</div>
      <div className="font-serif text-4xl gold mt-4 mb-6">{price}</div>
      <ul className="space-y-2 flex-1 mb-6">
        {features.map((f) => (
          <li key={f} className="text-sm text-gray-300 flex items-start gap-2">
            <span className="gold mt-1">◆</span>
            {f}
          </li>
        ))}
      </ul>
      <Link
        to={`/nettoyage?formule=${id}`}
        data-testid={`formula-cta-${id}`}
        className="btn-gold inline-flex items-center justify-center gap-2 w-full"
      >
        Réserver <ArrowRight size={14} />
      </Link>
    </div>
  );
}
